import { useTranslation } from 'react-i18next';
import { ActionIcon, clsx, Button, Select, CopyButton } from '@mantine/core';
import { IconPencil, IconSelector } from '@tabler/icons-react';
import { PropsWithChildren, useEffect } from 'react';
import dayjs from 'dayjs';
import { DebouncedState } from 'use-debounce';
import styles from './InspectionDetails.module.css';
import { STATUS_TRANSLATION_KEYS } from '../DashboardDrawer';
import {
  DashboardInspection,
  DashboardInspectionStatus,
} from '../../hooks/useInspectionList/types';
import { useCreateInspectionLink } from '../../hooks';

export interface InspectionDetailsProps {
  inspection: DashboardInspection;
  onOpenReview: (inspection: DashboardInspection) => void;
  onStatusChange: DebouncedState<(inspectionId: string, value: DashboardInspectionStatus) => void>;
}

function formatFullDate(value?: Date): string | null {
  return value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : null;
}

function NullableField({ children }: PropsWithChildren<unknown>) {
  return children ? <span>{children}</span> : <span className={styles['emptyValue']}>-</span>;
}

export function isInspectionReviewAvailable(inspection: DashboardInspection): boolean {
  return (
    [
      DashboardInspectionStatus.READY_FOR_REVIEW,
      DashboardInspectionStatus.PDF_SENT,
      DashboardInspectionStatus.FINALIZED,
    ] as (DashboardInspectionStatus | undefined)[]
  ).includes(inspection.status);
}

export function InspectionDetails({
  inspection,
  onOpenReview,
  onStatusChange,
}: InspectionDetailsProps) {
  const { t } = useTranslation();
  const { getInspectionLink } = useCreateInspectionLink();

  function getFilteredStatusOptions(status?: DashboardInspectionStatus) {
    if (!status) {
      return [];
    }

    const relevantStatuses = [
      DashboardInspectionStatus.READY_FOR_REVIEW,
      DashboardInspectionStatus.PDF_SENT,
      DashboardInspectionStatus.FINALIZED,
    ];

    const restrictToRelevant = relevantStatuses.includes(status);

    const allowedKeys = restrictToRelevant
      ? relevantStatuses
      : Object.keys(STATUS_TRANSLATION_KEYS).filter((k) => k !== 'all');

    return allowedKeys.map((key) => ({
      value: key,
      label: t(STATUS_TRANSLATION_KEYS[key as DashboardInspectionStatus]),
    }));
  }

  useEffect(
    () => () => {
      onStatusChange.flush();
    },
    [onStatusChange],
  );

  return (
    <div className={styles['container']}>
      <div className={styles['vinLabel']}>{t('inspectionList.table.vin')}</div>
      <div className={styles['vinContainer']}>
        <div className={styles['vin']}>{inspection.vin}</div>
        {isInspectionReviewAvailable(inspection) && (
          <ActionIcon variant='subtle' color='dark' onClick={() => onOpenReview(inspection)}>
            <IconPencil size={20} />
          </ActionIcon>
        )}
      </div>
      <div className={styles['divider']}></div>
      <div className={styles['row']}>
        <div className={styles['col']}>{t('inspectionList.table.status')}</div>
        <div className={clsx(styles['col'], styles['dropdownStatus'])}>
          <Select
            rightSectionWidth={25}
            rightSection={<IconSelector size='20px' />}
            styles={{ rightSection: { pointerEvents: 'none' } }}
            data={getFilteredStatusOptions(inspection.status)}
            defaultValue={inspection.status}
            onChange={(status) =>
              onStatusChange(inspection.inspectionId, status as DashboardInspectionStatus)
            }
          />
        </div>
      </div>
      <div className={styles['row']}>
        <div className={styles['col']}>{t('inspectionList.table.id')}</div>
        <div className={styles['col']}>
          <NullableField>{inspection.inspectionId}</NullableField>
        </div>
      </div>
      <div className={styles['row']}>
        <div className={styles['col']}>{t('inspectionList.table.lastUpdated')}</div>
        <div className={styles['col']}>
          <NullableField>{formatFullDate(inspection.lastUpdated)}</NullableField>
        </div>
      </div>
      <div className={styles['row']}>
        <div className={styles['col']}>{t('inspectionList.table.inspectionStart')}</div>
        <div className={styles['col']}>
          <NullableField>{formatFullDate(inspection.inspectionStart)}</NullableField>
        </div>
      </div>
      <div className={styles['row']}>
        <div className={styles['col']}>{t('inspectionList.table.inspectionEnd')}</div>
        <div className={styles['col']}>
          <NullableField>{formatFullDate(inspection.inspectionEnd)}</NullableField>
        </div>
      </div>
      <div className={styles['row']}>
        <div className={styles['col']}>{t('inspectionList.table.captureLink')}</div>
        <div className={styles['col']}>
          <CopyButton
            value={getInspectionLink({
              inspectionId: inspection.inspectionId,
            })}
          >
            {({ copied, copy }) => (
              <Button color={copied ? 'teal' : 'blue'} onClick={copy}>
                {copied ? 'Copied url' : 'Copy url'}
              </Button>
            )}
          </CopyButton>
        </div>
      </div>
    </div>
  );
}
